'use client';

import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { HttpStatusScreen } from '@/components/widget';
import { DESIGN_TOKENS_STYLE_ID, defaultTheme, themeToCssString } from '@/lib/theme';
import { sanitizePublicText } from '@/lib/sanitizePublicText';
import { store } from '@/redux/store';
import './globals.css';

type AppError = Error & { digest?: string };

/**
 * Renders when the root layout fails. Re-applies design tokens because the root
 * layout (and its `<style id={DESIGN_TOKENS_STYLE_ID}>`) is not mounted.
 */
export default function GlobalError({ error, reset }: { error: AppError; reset: () => void }) {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[GlobalError]', error);
    }
  }, [error]);

  const technicalHint =
    process.env.NODE_ENV !== 'production' ? sanitizePublicText(error.message) : undefined;

  return (
    <html lang="en" dir="ltr" suppressHydrationWarning>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <style
          id={DESIGN_TOKENS_STYLE_ID}
          dangerouslySetInnerHTML={{ __html: themeToCssString(defaultTheme) }}
        />
      </head>
      <body
        className="scroll-smooth overscroll-none font-sans antialiased"
        suppressHydrationWarning
      >
        <Provider store={store}>
          <HttpStatusScreen
            statusCode={500}
            digest={error.digest}
            technicalHint={technicalHint}
            onReset={reset}
            resetLabel="Try again"
          />
        </Provider>
      </body>
    </html>
  );
}
